import _ from "lodash";
import axios from "axios";
import moment from 'moment';
import Notifications from 'react-notification-system-redux';
import { shapeData } from './common';
import RahApi from '../Api/RahApi';
import { fetchTransactionsWithRules } from './transactionHelpers';
import {
  GET_BANK_ACCOUNTS,
  GET_CHART,
  GET_CONTACTS,
  GET_PROJECTS,
  SET_API_ERROR,
  GET_CLASSIFICATION,
  GET_TAX_PREFERENCE,
  LOAD_COMPLETED,
  GET_TRANSACTIONS,
  QUEUE_TRANSACTION_UPDATE,
  IS_SAVING,
  NEW_TRANSACTIONS,
  SET_POLL_INTERVAL,
  SET_LAST_SYNC,
  SET_ERROR,
  ROOT_URL,
  QUEUE_STATE } from "./types";

  const GENERIC_SYNC_ERROR = 'There was an error syncing to your RAH book';

  // This value is rendered straight to the user (TransactionList/TransactionListMenu),
  // so anything that is not a recognised RAH payload falls back to the generic
  // message rather than being displayed: raw backend errors can carry table names,
  // constraint names and book ids. Previously the whole string leaked, because
  // findIndex returns -1 when statusMessage is absent and -1 is truthy, making the
  // fallback unreachable. Splitting on ':' also truncated genuine RAH messages
  // ("E02: Single User mode" rendered as "E02-  Single User mode").
  // The RAH payload is not always at the top level. BankDataAPI only forwards an error
  // it recognises as retryable (queueFunctions.isRetryableError looks for statusCode
  // "1010" anywhere in the stored string), and the errors that satisfy that are
  // serialised request-promise StatusCodeErrors, which carry the RAH body under `error`:
  //   {"name":"StatusCodeError","statusCode":500,"error":{"statusCode":"1010","statusMessage":"E02: Single User mode"}}
  // Reading only the top level found no statusMessage in that shape, so every retryable
  // error fell back to the generic message. Both shapes are read, so a bare RAH payload
  // still works if the backend ever forwards one.
  const rahStatusMessage = (payload) => {
    if (!payload || typeof payload !== 'object') return undefined;
    const statusMessage = payload.statusMessage;
    return (typeof statusMessage === 'string' && statusMessage.length > 0) ? statusMessage : undefined;
  }

  const getRetryError = (message) => {
    if (!message) return null;

    const start = message.indexOf('{');
    if (start !== -1) {
      try {
        const parsed = JSON.parse(message.slice(start));
        const statusMessage = rahStatusMessage(parsed.error) || rahStatusMessage(parsed);
        if (statusMessage) return statusMessage;
      } catch (error) {
        return GENERIC_SYNC_ERROR;
      }
    }

    return GENERIC_SYNC_ERROR;
  }

  const shapeContacts = (response) => (
    response.data.map(contact => ({ 
      value: contact.ContactId.toUpperCase(), 
      label: contact.Description, 
      isCustomer: contact.IsCustomer, 
      isSupplier: contact.IsSupplier,
      isSuperfund: contact.IsSuperfund,
      isEmployee: contact.IsEmployee,
      isOther: contact.IsOther,
      isActive: contact.IsActive,
      DefaultTaxCode: contact.DefaultTaxCode
     }))
  );

  const shapeClassification = (response) => (
    response.data.map(classification => ({ 
      value: classification.ClassificationId.toUpperCase(),
      label: classification.Name,
      isDisabled: !classification.Status,
      isDefault: classification.IsDefault,
      isActive: classification.IsActive }))
  );

  const shapeBankAccounts = (response) => (
     (response.data.length > 0)
            ? response.data
              .filter(item => !item.FeedStatus || item.FeedStatus !== 'Deleted')
              .map(item => ({ 
                value: item.ClientAccountID, 
                label: item.AccountName, 
                bankId: item.BankAccountID,
                accountId: item.accountingCategoryId,
                isBankfeed: item.isBankfeed || item.ClientAccountID,
                RAHBankAccountID: item.RAHBankAccountID,
                R1BankAccountID: item.R1BankAccountID,
                isCreditCard: item.isCreditCard,
                lockoffDate: item.lockoffDate }))
            : []
  );

  const shapeChart = (response, RegisteredForTax = true) => {
    const isXPA = localStorage.getItem("cognitotoken");
    const isRah = (localStorage.getItem("authtype") === 'RAH') && !localStorage.getItem("cognitotoken");
    let chart = RegisteredForTax 
      ? response.data.chart
      : response.data.chart.map(item => {
        item.DefaultTaxCode = '';
        return item;
      });
    if (isXPA) {
      chart = chart.filter(item => !item.IsMaster)
        .sort((current, next) => {
          const currentCode = parseFloat(current.Code.replace('/', '.'));
          const nextCode = parseFloat(next.Code.replace('/', '.'));
          if (currentCode < nextCode)
            return -1;
          else
            return 1;
        });
    } else if (isRah) {
      chart = chart.sort((current, next) => {
        if (!current.RAHAccountType) return -1;
        if (current.RAHAccountType < next.RAHAccountType) return -1;
        if (current.RAHAccountType > next.RAHAccountType) return 1;
        if (parseInt(current.Code) < parseInt(next.Code)) return -1;
        if (parseInt(current.Code) > parseInt(next.Code)) return 1;
        return 0;
      });
    }

    const payload = { 
      taxCodes: response.data.taxCodes.map(item => ({ 
        value: item.Code, 
        label: `${item.Code} - ${item.Description}`, 
        TaxAccountListID: item.TaxAccountListID, 
        taxAccountId: item.TaxAccountID, 
        rate: item.Rate, 
        isPurchase: item.UseOnPurchases, 
        isSales: item.UseOnSales, 
        isActive: item.IsActive 
      })),
      chart: chart.map(item => ({ 
        value: item.Code, 
        label: item.Name, 
        defaultTaxCode: item.DefaultTaxCode, 
        accountId: item.AccountID, 
        accountType: item.AccountType, 
        description: item.Description, 
        RAHAccountType: item.RAHAccountType, 
        isActive: item.IsActive 
      }))
    };

    if (isRah) {
      payload.chart.unshift({ value: "match", label: "Match Transaction" });
    }
    payload.chart.unshift({ value: "-1", label: "Coded in R1", isActive: false }, { value: "-2", label: "Coded by Rule", isActive: false }, { value: "split", label: "Split Transaction" });

    return payload;
  }

  export function showError(message) {
    return Notifications.warning({ message });
  }

  export function loadData(bookId, authorizationtoken = localStorage.getItem("token"), cognitotoken = localStorage.getItem("cognitotoken")) {
    return function(dispatch) {
      const header = { headers: { authorizationtoken, cognitotoken, authtype: localStorage.getItem("authtype") } };

      const TaxAndChart = new Promise(async(resolve, reject) => {
        try {
           const taxResponse = await axios.get(`${ROOT_URL}/books/${bookId}/TaxPreferences/`, header);
           const { RegisteredForTax } = taxResponse.data;
           const charResponse = await axios.get(`${ROOT_URL}/books/${bookId}/chart/`, header)
           dispatch({ type: GET_CHART, payload: shapeChart(charResponse, RegisteredForTax) });
           dispatch({ type: GET_TAX_PREFERENCE, payload: RegisteredForTax });
           resolve();
        } catch (err) {
          if (process.env.BANKDATA_ENV !== 'production') {
            const errorMsg = err.response ? JSON.stringify(err.response.data) : err.toString();
            dispatch({ type: SET_ERROR, payload: errorMsg });
            reject();
          }
        }
      });  
      
      const bankaccounts = new Promise((resolve, reject) => {
        axios.get(`${ROOT_URL}/books/${bookId}/bankaccounts/`, header)
        .then(response => {
            dispatch({ type: GET_BANK_ACCOUNTS, payload: shapeBankAccounts(response) });
            resolve();
          })
          .catch((err) => {
            if (process.env.BANKDATA_ENV !== 'production') {
              const errorMsg = `GET: ${ROOT_URL}/books/${bookId}/bankaccounts/ -- Response: ${(err.response) ? JSON.stringify(err.response.data) : err.toString()}`;
              dispatch({ type: SET_ERROR, payload: errorMsg });
            }
            reject(err);
          });
        });

          const contacts = new Promise((resolve, reject) => {
            axios.get(`${ROOT_URL}/books/${bookId}/contacts/`, header)
            .then(response => {
                dispatch({ type: GET_CONTACTS, payload: shapeContacts(response) });
                resolve();
              })
              .catch((err) => {
                  if (process.env.BANKDATA_ENV !== 'production') {
                    const errorMsg = `GET: ${ROOT_URL}/books/${bookId}/contacts/ -- Response: ${(err.response) ? JSON.stringify(err.response.data) : err.toString()}`;
                    dispatch({ type: SET_ERROR, payload: errorMsg });
                  }
                 reject(err);
              });
            });

          const projects = new Promise((resolve, reject) => {
            axios.get(`${ROOT_URL}/books/${bookId}/projects/`, header)
            .then(response => {
                const payload = response.data.map(project => ({ value: project.ID, label: project.Name }));
                dispatch({ type: GET_PROJECTS, payload });
                resolve();
              })
              .catch((err) => {
                if (process.env.BANKDATA_ENV !== 'production') {
                  const errorMsg = `GET: ${ROOT_URL}/books/${bookId}/projects/ -- Response: ${(err.response) ? JSON.stringify(err.response.data) : err.toString()}`;
                  dispatch({ type: SET_ERROR, payload: errorMsg });
                }
                reject(err);
              });
            });
            
          const classifications = new Promise((resolve, reject) => {
            axios.get(`${ROOT_URL}/books/${bookId}/classifications/`, header)
            .then(response => {
                const payload = shapeClassification(response);
                dispatch({ type: GET_CLASSIFICATION, payload });
                resolve();
              })
              .catch((err) => {
                if (process.env.BANKDATA_ENV !== 'production') {
                  const errorMsg = `GET: ${ROOT_URL}/books/${bookId}/classifications/ -- Response: ${(err.response) ? JSON.stringify(err.response.data) : err.toString()}`;
                  dispatch({ type: SET_ERROR, payload: errorMsg });
                }
                reject(err);
              });
            }); 

        Promise.all([TaxAndChart, bankaccounts, contacts, projects, classifications])
        .then(() => dispatch({ type: LOAD_COMPLETED, payload: true }))
        .catch(error => {
            dispatch({ type: SET_API_ERROR, payload: true });
        });
    };
  }

  export function postTransfers(postData, postType, bookId, timeStamp, dateRange, account) {
    const method = (postType === 'export') ? 'post' : 'put';
    const timeLapse = moment().unix() - timeStamp;

    return function(dispatch) {
        dispatch({ type: IS_SAVING, payload: true });
        axios[method](`${ROOT_URL}/transactions/${(postType === 'export') ? 'transfer' : 'resettransfer'}`, postData,
        { headers: { cognitotoken: localStorage.getItem("cognitotoken"), authtype: localStorage.getItem("authtype") } })
          .then(async () => {
            try {
              const headers = { cognitotoken: localStorage.getItem("cognitotoken"), authtype: localStorage.getItem("authtype"), lastgetelapsed: timeLapse };
              const data = await fetchTransactionsWithRules(bookId, dateRange, account, headers);
              const transactionLength = data ? data.transactions.length : 0;
              const payload = data ? data.transactions.map((item, index) => shapeData(item, transactionLength, index)) : [];

              dispatch({ type: NEW_TRANSACTIONS, payload });
              dispatch({ type: IS_SAVING, payload: false });
            } catch (err) {
              dispatch(Notifications.warning({ message: 'Unable to export transactions' }));
              dispatch({ type: IS_SAVING, payload: false });
            }
      })
      .catch((error) => {
        console.log(' error: ', error);
        dispatch({ type: IS_SAVING, payload: false });
        dispatch(Notifications.warning({ message: 'Unable to export transactions' }));
      });
    };
  }

  export function importCSV(data, account, dateRange, closeModal) {
    return function(dispatch) {
      return new Promise((resolve, reject) => {
        axios.post(`${ROOT_URL}/books/upload`, data,
        { headers: { authorizationtoken: localStorage.getItem("token"), cognitotoken: localStorage.getItem("cognitotoken"), authtype: localStorage.getItem("authtype") } })
        .then((response) => {
          const importDetails = { clientAccountId: account.value, fileName: response.data.key, records: response.data.records };
            dispatch(importQIF(importDetails, account, dateRange, closeModal)).then(resolve, reject).catch((err) => console.log(err));
        })
        .catch((error) => {
          dispatch(Notifications.warning({ message: 'There was a problem uploading your file' }));
          reject();
        });
      })
    };
  }

  export function importQIF(importDetails, account, dateRange, closeModal) {
    return function(dispatch) {
        return new Promise((resolve, reject) => {
          axios.put(`${ROOT_URL}/books/upload`, importDetails,
          { headers: { authorizationtoken: localStorage.getItem("token"), cognitotoken: localStorage.getItem("cognitotoken"), authtype: localStorage.getItem("authtype") } })
          .then(async () => {
              dispatch(Notifications.removeAll());
              dispatch(Notifications.success({ message: 'Transactions imported' }));

              try {
                const headers = { authorizationtoken: localStorage.getItem("token"), cognitotoken: localStorage.getItem("cognitotoken"), authtype: localStorage.getItem("authtype") };
                const data = await fetchTransactionsWithRules(localStorage.getItem("bookid"), dateRange, account, headers);
                const transactionLength = data ? data.transactions.length : 0;
                let payload = data ? data.transactions.map((item, index) => shapeData(item, transactionLength, index, account.lockoffDate)) : [];
                dispatch({ type: NEW_TRANSACTIONS, payload, timeStamp: moment().unix() });
              } catch (err) {
                dispatch(Notifications.warning({ message: 'Unable to refresh transactions' }));
              } finally {
                closeModal();
                resolve();
              }
          })
          .catch((error) => {
            reject();
            dispatch(Notifications.warning({ message: 'Could not import transactions' }));
        });
      }) 
    };
  }

  export function postToRAH(bookId, startDate, endDate) {
    return function(dispatch) {
      dispatch({ type: SET_POLL_INTERVAL, payload: 1, syncing: true });
      RahApi.get(`${ROOT_URL}/RAH/${bookId}/sync?dateFrom=${startDate}&dateTo=${endDate}`, 
        { headers: { authorizationtoken: localStorage.getItem("token"), authtype: localStorage.getItem("authtype") } })
          .then(() => {
            dispatch(Notifications.success({ message: 'RAH synchronisation initiated' }));
      })
      .catch((error) => {
        dispatch(Notifications.warning({ message: 'Could not post to RAH' }));
      });
    };
  }

  export function retryQueueRAH(bookId) {
    return function(dispatch) {
      dispatch({ type: SET_POLL_INTERVAL, payload: 1, syncing: true });
      RahApi.get(`${ROOT_URL}/RAH/${bookId}/queue/retry`, 
        { headers: { authorizationtoken: localStorage.getItem("token"), authtype: localStorage.getItem("authtype") } })
          .then(() => {
            dispatch(Notifications.success({ message: 'Retrying RAH synchronisation initiated' }));
      })
      .catch((error) => {
        dispatch(Notifications.warning({ message: 'Could not post to RAH' }));
      });
    };
  }

  export const rahQueue = (bookId, dateRange, account, LastSync, timeStamp) => async dispatch => {
    try {
      const queue = (await axios.get(`${ROOT_URL}/RAH/${bookId}/queue`,
        { headers: { authorizationtoken: localStorage.getItem("token") } })).data;

      if (queue && queue.result === "success") {
        const timeLapse = moment().unix() - timeStamp;
        const { Queued: { syncItems, totalItems }, LastSync: { bankaccounts, chart, contact, classification, transactions, matchableItems, all }, Completed: { lastTransaction }, QueueState } = queue.queueState;
        const  headers = { authorizationtoken: localStorage.getItem("token"), authtype: localStorage.getItem("authtype") };
        const LastSyncUpdate = { matchableItems }
        LastSyncUpdate.all = all;


        // Post transaction check for last updated transaction
        if (!LastSync.transactions || moment(lastTransaction).isAfter(LastSync.transactions) || moment(transactions).isAfter(LastSync.transactions) || moment(matchableItems).isAfter(LastSync.matchableItems)) {
          if (!moment(matchableItems).isAfter(LastSync.matchableItems)) headers.lastgetelapsed = timeLapse;
          const response = await axios.get(`${ROOT_URL}/books/${bookId}/transactions/${dateRange}&account=${account.value}`, { headers });
          const transactionLength = response.data ? response.data.transactions.length : 0;
          let payload = response.data ? response.data.transactions.map((item, index) => shapeData(item, transactionLength, index, account.lockoffDate)) : [];
          const matches = response.data.matches || [];
          dispatch({ type: QUEUE_TRANSACTION_UPDATE, payload, matches, timeStamp: moment().unix() });
          LastSyncUpdate.transactions = moment(lastTransaction).isAfter(transactions) ? lastTransaction : transactions;
          dispatch({ type: SET_LAST_SYNC, payload: LastSyncUpdate });
        }

        // Handle posting errors
        QueueState.retryError = getRetryError(QueueState.retryError);
        dispatch({ type: QUEUE_STATE, payload: QueueState });

        // Sync action check and adjust poll time
        if (totalItems === 0) {
          return dispatch({ type: SET_POLL_INTERVAL, payload: 6, syncing: 0 });
        } else {
          if (syncItems === 0) {
            dispatch({ type: SET_POLL_INTERVAL, payload: 1, syncing: 0 });
          } else {
            dispatch({ type: SET_POLL_INTERVAL, payload: 1, syncing: syncItems });
          }

          if (!LastSync.bankaccounts || moment(bankaccounts).isAfter(LastSync.bankaccounts)) {
           const payload = shapeBankAccounts(await axios.get(`${ROOT_URL}/books/${bookId}/bankaccounts/`, { headers }));
           dispatch({ type: GET_BANK_ACCOUNTS, payload });
           LastSyncUpdate.bankaccounts = bankaccounts;
          }

          if (!LastSync.chart || moment(chart).isAfter(LastSync.chart)) {
            const payload = shapeChart(await axios.get(`${ROOT_URL}/books/${bookId}/chart/`, { headers }));
            dispatch({ type: GET_CHART, payload });
            LastSyncUpdate.chart = chart;
          }

          if (!LastSync.contact || moment(contact).isAfter(LastSync.contact)) {
            const payload = shapeContacts(await axios.get(`${ROOT_URL}/books/${bookId}/contacts/`, { headers }));
            dispatch({ type: GET_CONTACTS, payload });
            LastSyncUpdate.contact = contact;
          }

          if (!LastSync.classification || moment(classification).isAfter(LastSync.classification)) {
            const payload = shapeClassification(await axios.get(`${ROOT_URL}/books/${bookId}/classifications/`, { headers }));
            dispatch({ type: GET_CLASSIFICATION, payload });
            LastSyncUpdate.classification = classification;
          }
          // if (!LastSync.transactions || moment(transactions).isAfter(LastSync.transactions)) {
          //   const response = await axios.get(`${ROOT_URL}/books/${bookId}/transactions/${dateRange}`, { headers });
          //   const transactionLength = response.data[account.value] ? response.data[account.value].transactions.length : 0;
          //   let payload = response.data[account.value] ? response.data[account.value].transactions.map((item, index) => shapeData(item, transactionLength, index, account.lockoffDate)) : [];
          //   if (localStorage.getItem('uncodedOnly') === 'true') {
          //       payload = payload.map(item => {
          //       item.display = (!item.Coding[0] || item.Coding[0].CodingType === 'Rule') && !item.accountCodeId && !item.MatchId;
          //       return item
          //     })
          //   }
          //   const matches = response.data.matches || [];
          //   dispatch({ type: GET_TRANSACTIONS, payload, matches, timeStamp: moment().unix() });
          //   LastSyncUpdate.transactions = timeNow;
          // }
          dispatch({ type: SET_LAST_SYNC, payload: LastSyncUpdate });
        }
      }
    } catch (error) {
     console.log(error)
    }
  };

  // export function getReport(report) {
  //   return function(dispatch) {
  //     dispatch(Notifications.success({ message: 'Creating Report' }));
  //     axios.get(`${APS_URL}${(report === 'code') ? 'codeReport' : 'gstauditreport' }/${localStorage.getItem("bookid")}/?dateFrom=${localStorage.getItem("startdate")}&dateTo=${localStorage.getItem("enddate")}`,
  //     { headers: { 'authorizationtoken': localStorage.getItem('cognitotoken') }, responseType: 'blob'})
  //     .then(response => {
  //         const file = new Blob(
  //           [response.data], 
  //           {type: 'application/pdf'});
  //         const fileURL = URL.createObjectURL(file);
  //         window.open(fileURL);
  //         dispatch(Notifications.removeAll());
  //       })
  //       .catch((error) => {
  //         console.log(error)
  //       });
  //   };
  // }
  


// WEBPACK FOOTER //
// ./src/Actions/index.js